// Textos de disparo: biblioteca de textos prontos (LucraLink) e salvos pelo
// usuario (ate 50), com as variaveis do renderizador de templates e previa.

import { useState } from "react";
import { Copy, Loader2, Pencil, Save, Trash2 } from "lucide-react";
import {
  BROADCAST_TEXT_PRESETS,
  MAX_BROADCAST_TEXTS,
  MAX_BROADCAST_TEXT_BODY,
  MAX_BROADCAST_TEXT_NAME,
  TEMPLATE_VARIABLES,
  renderBroadcastText,
  type BroadcastLinkData,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { PlanGate } from "@/components/billing/PlanGate";
import { useBroadcastTexts, useDeleteBroadcastText, useSaveBroadcastText, type BroadcastText } from "@/lib/hooks/useBroadcastTexts";

export const SAMPLE_LINK: BroadcastLinkData = {
  title: "Fone Bluetooth com cancelamento de ruído",
  priceCents: 12990,
  originalPriceCents: 19990,
  coupon: "OFERTA10",
  url: "https://lucralink.com.br/r/Ab12Cd3",
  marketplaceCode: "shopee",
};

interface Draft {
  id: string | null;
  name: string;
  body: string;
}

const EMPTY_DRAFT: Draft = { id: null, name: "", body: "" };

function TextEditor({ draft, onChange, onSaved }: { draft: Draft; onChange: (d: Draft) => void; onSaved: (msg: string) => void }): JSX.Element {
  const save = useSaveBroadcastText();
  const [error, setError] = useState<string | null>(null);
  const valid = draft.name.trim().length > 0 && draft.body.trim().length > 0;

  async function handleSave(): Promise<void> {
    setError(null);
    try {
      await save.mutateAsync(draft);
      onSaved(draft.id ? "Texto atualizado." : "Texto salvo.");
      onChange(EMPTY_DRAFT);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar o texto.");
    }
  }

  return (
    <Card variant="raised">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>{draft.id ? "Editar texto" : "Novo texto"}</CardTitle>
        <CardDescription>Use as variáveis: elas são preenchidas com o link escolhido na hora de inserir no post.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5 pt-5 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="text-name" variant="field">Nome</Label>
            <Input id="text-name" value={draft.name} maxLength={MAX_BROADCAST_TEXT_NAME} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="text-body" variant="field">Texto</Label>
            <textarea
              id="text-body"
              rows={8}
              value={draft.body}
              maxLength={MAX_BROADCAST_TEXT_BODY}
              onChange={(e) => onChange({ ...draft, body: e.target.value })}
              className="flex w-full resize-y rounded-md border bg-[hsl(var(--input))] px-3 py-2 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" aria-label="Variáveis">
            {TEMPLATE_VARIABLES.map((v) => (
              <button key={v} type="button" onClick={() => onChange({ ...draft, body: `${draft.body}{${v}}` })} className="rounded border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground hover:text-foreground">
                {`{${v}}`}
              </button>
            ))}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button type="button" disabled={!valid || save.isPending} onClick={() => void handleSave()}>
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
            </Button>
            {draft.id && <Button type="button" variant="ghost" onClick={() => onChange(EMPTY_DRAFT)}>Cancelar</Button>}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Prévia com um produto de exemplo</p>
          <pre className="min-h-40 whitespace-pre-wrap rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.35)] p-3 font-sans text-sm text-foreground">
            {draft.body.trim() ? renderBroadcastText(draft.body, SAMPLE_LINK) : "Escreva o texto para ver a prévia."}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}

function SavedList({ texts, onEdit, onMessage }: { texts: BroadcastText[]; onEdit: (t: BroadcastText) => void; onMessage: (msg: string) => void }): JSX.Element {
  const remove = useDeleteBroadcastText();
  if (texts.length === 0) return <p className="text-sm text-muted-foreground">Você ainda não salvou nenhum texto.</p>;
  return (
    <ul className="space-y-2">
      {texts.map((t) => (
        <li key={t.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{t.name}</p>
            <p className="line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{t.body}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button size="icon-sm" variant="ghost" aria-label={`Editar ${t.name}`} onClick={() => onEdit(t)}>
              <Pencil size={13} />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Excluir ${t.name}`}
              disabled={remove.isPending}
              onClick={() => remove.mutateAsync(t.id).then(() => onMessage("Texto excluído."), (e: unknown) => onMessage(e instanceof Error ? e.message : "Erro ao excluir."))}
            >
              <Trash2 size={13} />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TextosDisparoPageContent(): JSX.Element {
  const { data: texts = [], isError } = useBroadcastTexts();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [message, setMessage] = useState<string | null>(null);
  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader title="Textos de disparo" subtitle="Textos prontos e salvos para inserir no Criar Posts e no envio de ofertas." />
      {message && <p role="status" className="text-sm text-primary">{message}</p>}
      <TextEditor draft={draft} onChange={setDraft} onSaved={setMessage} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card variant="raised">
          <CardHeader className="border-b border-border">
            <CardTitle>Meus textos ({texts.length}/{MAX_BROADCAST_TEXTS})</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {isError ? <p role="alert" className="text-sm text-destructive">Não foi possível carregar seus textos.</p> : <SavedList texts={texts} onEdit={(t) => setDraft({ id: t.id, name: t.name, body: t.body })} onMessage={setMessage} />}
          </CardContent>
        </Card>
        <Card variant="raised">
          <CardHeader className="border-b border-border">
            <CardTitle>Textos prontos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-4">
            {BROADCAST_TEXT_PRESETS.map((p) => (
              <div key={p.key} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                  <p className="line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{p.body}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setDraft({ id: null, name: `${p.name} (cópia)`.slice(0, MAX_BROADCAST_TEXT_NAME), body: p.body })}>
                  <Copy size={12} /> Usar como base
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Textos de disparo: parte do disparo em massa, PRO (gate central; enforcement no banco). */
export function TextosDisparoPage(): JSX.Element {
  return (
    <PlanGate feature="bulkSend" title="Textos de disparo" subtitle="Textos prontos e salvos para inserir no Criar Posts e no envio de ofertas.">
      <TextosDisparoPageContent />
    </PlanGate>
  );
}
